import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { MlModelListParams, modelApi } from '@/api/modelApi'
import { getPageByUrl } from '@/api/vpApi'
import { MLModel, MLModelStatus, MLModelType } from '@/types/model'
import { PaginatedResponse } from '@/types/pagination'
import { objectToQueryString } from '@/utils/url'

type AppModelListQueryParams = {
  limit?: number
  ordering?: string
  status?: MLModelStatus | MLModelStatus[]
  id?: string
  model_type?: MLModelType
}

export const modelQueryKeys = {
  all: ['models'] as const,
  list: (params: MlModelListParams) =>
    [...modelQueryKeys.all, 'list', params] as const,
  listAppMLModels: (appId: string, params: AppModelListQueryParams) =>
    [...modelQueryKeys.all, 'app-ml-models', appId, params] as const,
  details: (modelId: string) =>
    [...modelQueryKeys.all, 'details', modelId] as const
}

export const useGetMlModelList = (params: MlModelListParams) => {
  return useQuery({
    queryKey: modelQueryKeys.list(params),
    queryFn: () => modelApi.getMlModelList(params),
    placeholderData: keepPreviousData
  })
}

export const useGetAppMLModels = (
  appId: string,
  params?: AppModelListQueryParams
) => {
  const queryParams = {
    limit: 100,
    ordering: 'created_at',
    ...params
  }

  const queryString = objectToQueryString(queryParams)
  const initialPageParam = `/v1/applications/${appId}/ml-models/?${queryString}`

  return useInfiniteQuery({
    queryKey: modelQueryKeys.listAppMLModels(appId, queryParams),
    queryFn: ({ pageParam }) =>
      getPageByUrl<PaginatedResponse<MLModel[]>>(pageParam),
    getNextPageParam: (lastPage) => {
      return lastPage.next ?? undefined
    },
    initialPageParam
  })
}

export const useGetModelDetails = (modelId: string, enabled?: boolean) => {
  return useQuery({
    queryKey: modelQueryKeys.details(modelId),
    queryFn: () => modelApi.getModelDetails(modelId),
    enabled
  })
}

export const useCreateModel = () => {
  return useMutation({
    mutationFn: modelApi.createModel
  })
}

export const useUpdateModel = (modelId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: modelApi.updateModel,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: modelQueryKeys.details(modelId)
      })
  })
}

export const useInvalidateModelDetails = (modelId: string) => {
  const queryClient = useQueryClient()

  const invalidateModelDetails = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: modelQueryKeys.details(modelId)
    })
  }, [queryClient, modelId])

  return {
    invalidateModelDetails
  }
}

export const useDeleteModel = () => {
  return useMutation({
    mutationFn: modelApi.deleteModel
  })
}
